import React, { useCallback, useEffect, useState } from 'react';
import { InhalerIcon, ZapIcon, DropletIcon, HeartIcon, PillIcon, IconProps } from '../components/icons';
import { Tint } from './mock';
export type Condition = {
  id: string;
  label: string;
  programme: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
};
export const CONDITIONS: Condition[] = [{
  id: 'asthma',
  label: 'Asthma',
  programme: 'Living well with asthma',
  icon: InhalerIcon,
  tint: 'sky'
}, {
  id: 'epilepsy',
  label: 'Epilepsy',
  programme: 'Living well with epilepsy',
  icon: ZapIcon,
  tint: 'butter'
}, {
  id: 'sickle-cell',
  label: 'Sickle Cell',
  programme: 'Living well with sickle cell',
  icon: DropletIcon,
  tint: 'coral'
}, {
  id: 'mental-health',
  label: 'Mental Health',
  programme: 'Your mental health journey',
  icon: HeartIcon,
  tint: 'lilac'
}, {
  id: 'substance-use',
  label: 'Substance Use',
  programme: 'Your recovery journey',
  icon: PillIcon,
  tint: 'sage'
}];
const STORAGE_KEY = 'dhp.conditions.v1';
const DEFAULT_SELECTED = ['asthma', 'epilepsy'];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
let cached: string[] | null = null;
function load(): string[] {
  if (cached) return cached;
  if (typeof window === 'undefined') return DEFAULT_SELECTED;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      // keep only ids that still exist
      const valid = parsed.filter(id => CONDITIONS.some(c => c.id === id));
      cached = valid;
      return valid;
    }
  } catch (e) {
    console.error('Failed to load conditions', e);
  }
  cached = DEFAULT_SELECTED;
  return DEFAULT_SELECTED;
}
function save(ids: string[]) {
  cached = ids;
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  }
  emit();
}
export function useConditions() {
  const [selected, setSelected] = useState<string[]>(load);
  useEffect(() => {
    const sync = () => setSelected(load());
    listeners.add(sync);
    return () => {
      listeners.delete(sync);
    };
  }, []);
  const toggle = useCallback((id: string) => {
    const current = load();
    const next = current.includes(id) ? current.filter(c => c !== id) : [...current, id];
    save(next);
  }, []);
  const isSelected = useCallback((id: string) => selected.includes(id), [selected]);
  // selected condition objects, in CONDITIONS display order
  const selectedConditions = CONDITIONS.filter(c => selected.includes(c.id));
  return {
    selected,
    selectedConditions,
    toggle,
    isSelected
  };
}